import React, { FC, useMemo } from 'react';

import { Typography } from '@mui/material';
import { styled } from '@mui/material';

import useTenantTranslation from '~/hooks/useTenantTranslation';
import GlossaryGridHit from '~/pages/DiscoverPage/components/GlossaryGridHit/GlossaryGridHit';
import Hit from '~/pages/DiscoverPage/components/Hit/Hit';
import { AggregatorHit, SendHitsEvent } from '~/pages/DiscoverPage/const';
import { sendAlgoliaClickEvent } from '~/pages/DiscoverPage/helpers';
import { LARGE_MARGIN_PX, SMALL_MARGIN, SMALL_MARGIN_PX } from '~/theme';
import getAttributeByLocale from '~/utils/getAttributeByLocale';

type HitsObj = {
  [x: string]: any[];
};

const groupByLetterIndex = (hits: AggregatorHit[], locale: string): HitsObj => {
  const obj = {};

  hits.forEach((hit) => {
    const letterIndex = getAttributeByLocale(hit?.title, locale)?.slice(0, 1)?.toUpperCase();
    if (letterIndex && letterIndex in obj) {
      obj[letterIndex].push(hit);
    } else {
      obj[letterIndex] = [hit];
    }
  });

  return obj;
};

interface GlossaryInfiniteHitsProps {
  hits: AggregatorHit[];
  isGridView?: boolean;
  sendEvent: SendHitsEvent;
}

const GlossaryInfiniteHits: FC<GlossaryInfiniteHitsProps> = ({
  hits,
  isGridView = false,
  sendEvent,
}) => {
  const { i18n } = useTenantTranslation();
  const structuredHits = useMemo(() => {
    const grouped = groupByLetterIndex(hits || [], i18n.language);
    const entries = Object.entries(grouped);

    if (isGridView) {
      return entries;
    }

    return entries.flatMap((entry) => entry.flat());
  }, [hits, isGridView, i18n.language]);

  if (isGridView) {
    return structuredHits.map(([letter, hits]) => (
      <LetterBlock key={`index-${letter}`}>
        <Letter as={Typography}>{letter}</Letter>
        <HitsBlock>
          {hits?.map((hit: AggregatorHit) => {
            return (
              <GlossaryGridHit
                key={hit?.id}
                hit={hit}
                onClick={() => sendAlgoliaClickEvent(sendEvent, hit)}
              />
            );
          })}
        </HitsBlock>
      </LetterBlock>
    ));
  }

  return structuredHits
    ? structuredHits.map((item: AggregatorHit | string) => {
        const isLetter = typeof item === 'string';

        if (isLetter) {
          return (
            <Letter key={item} className={'hit-letter'}>
              {item}
            </Letter>
          );
        }

        return (
          <li key={item.id}>
            <Hit hit={item} onClick={() => sendAlgoliaClickEvent(sendEvent, item)} />
          </li>
        );
      })
    : null;
};

export default GlossaryInfiniteHits;

const Letter = styled('li', {
  shouldForwardProp: (prop) => prop !== 'as',
})`
  font-weight: bold;
  font-size: 32px;
  line-height: 38px;
  border-bottom: 3px solid ${({ theme }) => theme.palette.common.violetAnalyticsCard};
  padding-bottom: ${SMALL_MARGIN / 2}px;

  ${({ theme }) => theme.breakpoints.up('md')} {
    font-size: 40px;
    line-height: 48px;
    margin-bottom: ${LARGE_MARGIN_PX};
  }
`;
const LetterBlock = styled('li')`
  width: 100%;
`;
const HitsBlock = styled('div')`
  margin-bottom: ${LARGE_MARGIN_PX};
  display: flex;
  flex-wrap: wrap;
  margin-right: -${SMALL_MARGIN_PX};
  width: 100%;

  & > div {
    width: calc((100% - ${SMALL_MARGIN * 3}px) / 3);
    margin-right: ${SMALL_MARGIN_PX};
  }
`;
